/**
 * CAD 资产插件 —— 管理 CAD 图纸的加载、挂载、渲染、拾取、编辑、选择全生命周期
 *
 * 职责：
 * 1. 实现 AssetPlugin + ExportableAssetPlugin，提供资产 CRUD 和导出
 * 2. 实现 ActivatableToolPlugin，接入引擎工具互斥体系
 * 3. 实现 Editable，桥接 CAD HistoryManager 提供撤销/重做
 * 4. 实现 Selectable，桥接 CAD EntitySelector 提供选择同步
 * 5. 创建 Inversify 容器桥接 EngineContext 服务到 CAD 内部
 * 6. 注册 InputConsumer，通过 InputRouter 接收输入事件
 * 7. 作为 RenderContributor / PickProvider 参与渲染管线和拾取
 */

import type { Container } from 'inversify'
import { cloneDeep, isNil, omitBy } from 'lodash-es'
import { nanoid } from 'nanoid'
import * as THREE from 'three'
import { Text } from 'troika-three-text'
import { DUAL_BACKEND_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import type {
  ICadEntityDetector,
  ICadEntitySelector,
  ICadHistoryManager,
  ICadViewManager,
} from '@/plugins/cad/module/api'
import type { CADController } from '@/plugins/cad/module/CADModule/CADController'
import { EntityType } from '@/plugins/cad/module/CADModule/model/common/constant'
import {
  loadCadWebGpuLineModule,
  registerCadWebGpuLineModule,
  setCadLineBackend,
} from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import {
  loadCadWebGpuTextMaterialModule,
  registerCadWebGpuTextMaterialModule,
  setCadTextBackend,
} from '@/plugins/cad/module/CADModule/object/text/CadTextFactory'
import type {
  IDrawToolParams,
  IEditToolParams,
} from '@/plugins/cad/module/CADModule/toolDispatcher/interface'
import { DrawToolType, EditToolType } from '@/plugins/cad/module/CADModule/toolDispatcher/interface'
import { createCadContainer } from '@/plugins/cad/module/container/container'
import { TYPES as CAD_TYPES } from '@/plugins/cad/module/container/types'
import { StartMode } from '@/plugins/cad/module/core/common/interface'
import type { BaseTool } from '@/plugins/cad/module/core/tool/BaseTool'
import { submitSceneInView } from '@/plugins/shared/submitSceneInView'
import type { EngineContext } from '@/runtime/EngineContext'
import { ExporterRegistry } from '@/runtime/ExporterRegistry'
import { LoaderRegistry } from '@/runtime/LoaderRegistry'
import { DisposableScope } from '@/shared/disposable/DisposableScope'
import { getLoggerManager } from '@/shared/logging'
import type { ExportOptions, PhaseRenderContext } from '@/shared/types/assets'
import { RenderPhase } from '@/shared/types/assets'
import type {
  AssetId,
  CadEntityData,
  CadMetadata,
  ClipVolume,
  Editable,
  PickQuery,
  PickResult,
  Selectable,
} from '@/shared/types/core'
import type {
  ActivatableToolPlugin,
  ActivationToken,
  ExportableAssetPlugin,
} from '@/shared/types/plugin'
import type {
  CadLayer,
  ViewEventRouter,
  ViewInputConsumer,
  ViewKeyEvent,
  ViewPointerEvent,
  ViewRegistry,
} from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'
import { generateId, setMaskRecursive } from '@/shared/utils'
import {
  startCadDashScaleTracking,
  stopCadDashScaleTracking,
} from './diagnostics/CadDashScaleTracker'
import { CadPerformanceDiagnostics } from './diagnostics/CadPerformanceDiagnostics'
import type { MetadataProvider } from './exporters/DxfCadExporter'
// 内建 Exporters
import { DxfCadExporter } from './exporters/DxfCadExporter'
// 内建 Loaders
import { DxfCadLoader } from './loaders/DxfCadLoader'
import { JsonCadLoader } from './loaders/JsonCadLoader'
import type { CaptureHelper } from './module/CADModule/auxiliaryTool/captureHelper'
import type { GridHelper } from './module/CADModule/auxiliaryTool/gridHelper'
import type { OrthogonalHelper } from './module/CADModule/auxiliaryTool/OrthogonalHelper'
import type { PointCloudHelper } from './module/CADModule/auxiliaryTool/pointCloudHelper'
import type { PolarHelper } from './module/CADModule/auxiliaryTool/polarHelper'
import { AddEntityCommand } from './module/CADModule/command/AddEntityCommand'
import { CompositeCommand } from './module/CADModule/command/CompositeCommand'
import { DeleteEntityCommand } from './module/CADModule/command/DeleteEntityCommand'
import {
  CreateLayerCommand,
  RemoveLayerCommand,
  UpdateLayerCommand,
} from './module/CADModule/command/LayerCommand'
import { UpdateEntityCommand } from './module/CADModule/command/UpdateEntityCommand'
import type { CADViewManager } from './module/CADModule/manager/CADViewManager'
import type { DataManager, IPlane, IPlaneMetaData } from './module/CADModule/manager/DataManager'
import type { ObjectManager } from './module/CADModule/manager/ObjectManager'
import type { IEntity } from './module/CADModule/model/entity/BaseEntity'
import { type ILayer, Layer } from './module/CADModule/model/layer/Layer'
import type { IUpdateOptions } from './module/CADModule/object/objectManager/BaseObjectManager'
import {
  collectCadEntityStats,
  formatCadEntityStats,
} from './module/CADModule/utils/cadDiagnostics'
import type { CaptureType, ICaptureConfig } from './module/core/auxiliaryTool/Capturer'
import { DxfParser } from './module/parser/DxfParser'
// 本地类型
import type {
  CadHandle,
  CadLayerCreateInput,
  CadLayerMutationOptions,
  CadLayerUpdatePatch,
  CadLoadRequest,
  CadLoadResult,
  CadPluginOptions,
} from './types'

const log = getLoggerManager().getLogger('cad')
const diagnosticsLog = getLoggerManager().getLogger('cad:diagnostics')

// ==================== 错误类型 ====================

/** 格式不支持错误，当没有任何 Loader 能处理该请求时抛出 */
export class FormatNotSupportedError extends Error {
  /** 触发错误的资源 URL 或占位描述 */
  readonly url: string
  /** 已注册可用的格式列表 */
  readonly formats: string[]

  constructor(url: string, formats: string[]) {
    super(`无法加载 "${url}"：不支持该格式。已注册格式: ${formats.join(', ')}`)
    this.name = 'FormatNotSupportedError'
    this.url = url
    this.formats = formats
  }
}

/** InputConsumer 优先级：高于相机控制(50)，低于测量工具(100) */
const CAD_INPUT_PRIORITY = 90

const resolveCadToolName = (toolName: unknown): string | symbol | null => {
  if (typeof toolName === 'string' || typeof toolName === 'symbol') {
    return toolName
  }
  return null
}

const getCadHandleEntities = (handle: CadHandle): readonly IEntity[] => {
  const entities = handle.dataManager?.entities
  return Array.isArray(entities) ? entities : []
}

function collectVisibleCadRenderables(root: THREE.Object3D): {
  count: number
  textObjects: { total: number; visible: number; outlines: number }
  byEntityType: Record<string, { renderables: number; drawCalls: number }>
  batchCount: number
  materialBatches: Record<string, number>
} {
  let count = 0
  const byEntityType: Record<string, { renderables: number; drawCalls: number }> = {}
  let batchCount = 0
  const materialBatches: Record<string, number> = {}
  const textObjects = { total: 0, visible: 0, outlines: 0 }
  root.traverse((child) => {
    if (child instanceof Text) textObjects.total += 1
  })
  root.traverseVisible((child) => {
    const candidate = child as THREE.Object3D & { geometry?: unknown; material?: unknown }
    if (!child.visible || candidate.geometry == null || candidate.material == null) {
      return
    }
    count += 1
    if (child instanceof Text) {
      textObjects.visible += 1
      if (child.userData.cadTextOutline) textObjects.outlines += 1
    }
    const batch = child.userData?.cadBatch === true
    if (batch) batchCount += 1
    const ranges = child.userData?.cadBatchEntityRanges as
      | Array<{ entityType?: string }>
      | undefined
    if (batch && ranges?.length) {
      const entityTypes = new Set(ranges.map((range) => String(range.entityType ?? 'unknown')))
      for (const entityType of entityTypes) {
        const current = byEntityType[entityType] ?? { renderables: 0, drawCalls: 0 }
        current.renderables += 1
        current.drawCalls += 1
        byEntityType[entityType] = current
      }
    } else {
      const entityType = String(child.userData?.entity?.type ?? 'unknown')
      const current = byEntityType[entityType] ?? { renderables: 0, drawCalls: 0 }
      current.renderables += 1
      current.drawCalls += 1
      byEntityType[entityType] = current
    }
    const material = candidate.material as { uuid?: string; id?: number } | undefined
    const materialKey = String(
      child.userData?.cadBatchStyleKey ?? material?.uuid ?? material?.id ?? 'unknown'
    )
    materialBatches[materialKey] = (materialBatches[materialKey] ?? 0) + 1
  })
  return { count, byEntityType, batchCount, materialBatches, textObjects }
}

// ==================== CadPlugin ====================

/** CAD 资产插件 */
export class CadPlugin
  implements
    ExportableAssetPlugin<CadLoadRequest, CadHandle>,
    ActivatableToolPlugin,
    Editable,
    Selectable
{
  /** 插件唯一名称，用作注册 key */
  readonly name = 'cad'
  readonly graphicsRequirements = DUAL_BACKEND_GRAPHICS_REQUIREMENTS
  /** 资产 kind 标识，参与事件分发 */
  readonly kind = 'cad' as const
  /** 插件启动优先级，数值大者先启动 */
  readonly priority = 40
  /** 订阅的渲染阶段 */
  readonly phases = [RenderPhase.MainOpaque, RenderPhase.Overlay3D]
  /** 同 phase 内渲染排序优先级（CAD 后于点云渲染，确保深度正确） */
  readonly renderPriority = 10
  /** 依赖的服务列表，未就绪时插件无法启动 */
  readonly dependencies = [
    'CameraRig',
    'ViewportCamera',
    'SelectionService',
    'RenderPipeline',
    'PickingService',
    'ViewEventRouter',
    'ViewRegistry',
  ]
  /** 对外提供的服务列表 */
  readonly provides = ['CadService']
  /** 工具分组，与测量等工具互斥 */
  readonly group = 'default'

  /** 加载器注册表 */
  private readonly loaders: LoaderRegistry<CadLoadRequest, CadLoadResult>

  /** 导出器注册表 */
  private readonly exporters: ExporterRegistry<CadHandle, Blob>

  /** 引擎上下文，onInit 时注入 */
  private context!: EngineContext

  /** 全局唯一 CAD Inversify 容器 */
  private cadContainer!: Container

  /** 已加载资产映射 */
  private assets = new Map<AssetId, CadHandle>()

  /** 当前活跃会话 ID（接收编辑输入的资产） */
  private activeSessionId: AssetId | null = null

  /** 正在进行的加载任务，支持中止 */
  private pendingLoads = new Map<AssetId, AbortController>()

  /** 插件是否已销毁 */
  private disposed = false

  /** 桥接事件的清理函数列表，onDestroy 时统一调用 */
  private bridgeCleanups: (() => void)[] = []

  /** 选择同步锁，防止 CAD↔Engine 选择事件循环触发 */
  private bridgingSelection = false

  /** 渲染期临时隔离 cadRoot，避免 replay 整棵 mainScene。 */
  private readonly isolatedCadScene = new THREE.Scene()

  /** 帧级 CAD 性能诊断，默认是无开销 no-op 路径。 */
  private readonly performanceDiagnostics = new CadPerformanceDiagnostics()
  private readonly staticBatchingEnabled: boolean

  // ─── ToolPlugin 状态 ─────────────────────────────────────

  /** ActivationToken，由 PluginManager 注入 */
  private _activationToken: ActivationToken | null = null

  /** 工具是否激活 */
  private _isActive = false

  /** View-local InputConsumer 实例 */
  private _inputConsumer: ViewInputConsumer | null = null

  /** 工具当前是否处于激活态 */
  get isActive(): boolean {
    return this._isActive
  }

  constructor(options?: CadPluginOptions) {
    this.staticBatchingEnabled = options?.staticBatching ?? true
    this.loaders = new LoaderRegistry()
    this.exporters = new ExporterRegistry()

    // 内建 Loader：先注册默认的 DxfCadLoader 和 JsonCadLoader
    const builtinLoaders = [new DxfCadLoader(), new JsonCadLoader()]
    const extraLoaders = options?.extraLoaders ?? []
    // extraLoaders 中已覆盖的格式不再注册内建实现
    const overriddenFormats = new Set(extraLoaders.map((l) => l.format))
    for (const loader of builtinLoaders) {
      if (!overriddenFormats.has(loader.format)) {
        this.loaders.register(loader)
      }
    }

    // 注册用户自定义 Loader（可覆盖内建实现）
    for (const loader of extraLoaders) {
      this.loaders.register(loader)
    }

    // 先注册用户自定义 Exporter，优先级高于内建实现
    const extraExporters = options?.extraExporters ?? []
    const userFormats = new Set(extraExporters.map((e) => e.format))
    for (const exporter of extraExporters) {
      this.exporters.register(exporter)
    }

    // 内建 Exporter：仅当用户未占用该 format 时才补注册（闭包延迟获取容器中的 DataManager）
    if (!userFormats.has('dxf')) {
      const getMetadata: MetadataProvider = (_handle) => {
        const viewManager = this.cadContainer.get<ICadViewManager>(CAD_TYPES.CADViewManager)
        return viewManager.dataManager.serialize()
      }
      this.exporters.register(new DxfCadExporter(getMetadata))
    }
  }

  // ─── Plugin 生命周期 ─────────────────────────────────────

  /**
   * 初始化：保存上下文，创建 CAD 容器，注册 CadService。
   *
   * @param context - 引擎上下文
   */
  /**
   * 预加载 WebGPU 宽线与文本材质模块。
   *
   * `three/webgpu` 不能进默认（WebGL2）产物，而 CAD 的线创建点是同步的，因此这里在 WebGPU
   * 下预加载 `Line2NodeMaterial` 并注册进 `CadLineFactory` 的同步槽位。
   */
  async preload(context: EngineContext): Promise<void> {
    const kind = context.graphics?.kind ?? 'webgl2'
    setCadLineBackend(kind)
    setCadTextBackend(kind)
    if (kind !== 'webgpu') {
      return
    }
    const [lineModule, textMaterialModule] = await Promise.all([
      loadCadWebGpuLineModule(),
      loadCadWebGpuTextMaterialModule(),
    ])
    registerCadWebGpuLineModule(lineModule)
    registerCadWebGpuTextMaterialModule(textMaterialModule)
  }

  /** 预加载失败/回滚时清空同步槽位并退回 WebGL2 语义。 */
  rollbackPreload(): void {
    registerCadWebGpuLineModule(null)
    registerCadWebGpuTextMaterialModule(null)
    setCadLineBackend('webgl2')
    setCadTextBackend('webgl2')
  }

  onInit(context: EngineContext): void {
    this.initialize(context, true)
  }

  /**
   * 供轻量 CAD 代理初始化完整实现，不重复注册 CadService。
   *
   * @internal
   * @param context - 引擎上下文
   */
  onInitAsDelegate(context: EngineContext): void {
    this.initialize(context, false)
  }

  private initialize(context: EngineContext, registerService: boolean): void {
    this.context = context
    this.cadContainer = createCadContainer(context)
    if (registerService) {
      context.registerService('CadService', this)
    }
    log.debug(
      `[CadPlugin] initialized, loaders=${this.loaders.formats().join(',')}, exporters=${this.exporters.formats().join(',')}`
    )
  }

  /** 启动：注册为渲染贡献者和拾取提供者，桥接选择服务 */
  onStart(): void {
    const pipeline = this.context.getService('RenderPipeline')
    pipeline.addRenderContributor(this)

    const picking = this.context.getService('PickingService')
    picking.addProvider(this)

    if (this.context.hasService('UnitPresentationService')) {
      const unitPresentation = this.context.getService('UnitPresentationService')
      this.bridgeCleanups.push(
        unitPresentation.subscribe(() => {
          for (const handle of this.assets.values()) {
            handle.objectManager.refreshUnitPresentation?.()
          }
          this.context.renderInvalidation.invalidate('cad.unitPreference.changed')
        })
      )
    }

    // 条件订阅裁切体变更（ClipPlugin 可能未注册，可选依赖）
    if (this.context.hasService('ClipService')) {
      this.context.disposables.track(
        this.context
          .getService('ClipService')
          .onChanged((volumes) => this._applyClipVolumes(volumes))
      )
    }

    this.bridgeSelection()
    log.debug('[CadPlugin] started, renderContributor=true, pickProvider=true')
  }

  /** 销毁：中止所有加载、释放所有资产、移除注册、清理容器 */
  onDestroy(): void {
    if (this.pendingLoads.size > 0 || this.assets.size > 0) {
      log.debug(
        `[CadPlugin] destroying, pendingLoads=${this.pendingLoads.size}, assets=${this.assets.size}`
      )
    }
    // 若工具激活中，先反激活
    if (this._isActive) {
      this._deactivateInternal()
    }

    // 移除渲染贡献者和拾取提供者注册
    const pipeline = this.context.getService('RenderPipeline')
    pipeline.removeRenderContributor(this)
    const picking = this.context.getService('PickingService')
    picking.removeProvider(this)

    // 中止所有在途加载
    for (const [id, controller] of this.pendingLoads) {
      controller.abort()
      log.debug(`[CadPlugin] onDestroy aborted pending load, id=${id}`)
    }
    this.pendingLoads.clear()

    this.disposed = true

    // 清理桥接事件监听
    for (const cleanup of this.bridgeCleanups) {
      try {
        cleanup()
      } catch {
        /* 忽略清理异常 */
      }
    }
    this.bridgeCleanups.length = 0

    // 释放所有资产
    this.dispose()

    try {
      const viewManager = this.cadContainer?.get<ICadViewManager>(CAD_TYPES.CADViewManager) as
        | (ICadViewManager & { dispose?: () => void })
        | undefined
      viewManager?.dispose?.()
    } catch {
      /* CADViewManager 不可用时忽略 */
    }

    // 清理 Inversify 容器
    if (this.cadContainer) {
      this.cadContainer.unbindAll()
    }
  }

  // ─── ActivatableToolPlugin 接口 ─────────────────────────

  /**
   * 由 PluginManager 注入合法 token。
   *
   * @param token - 激活令牌
   */
  setActivationToken(token: ActivationToken): void {
    this._activationToken = token
  }

  /**
   * 激活 CAD 工具：注册 InputConsumer，启动 CADController。
   *
   * @param token - 激活令牌，必须与注入值一致
   */
  activate(token: ActivationToken): void {
    this._validateToken(token)
    if (this._isActive) return

    this._isActive = true
    this._activateInternal()
  }

  /**
   * 反激活 CAD 工具：移除 InputConsumer，退出 CADController。
   *
   * @param token - 激活令牌，必须与注入值一致
   */
  deactivate(token: ActivationToken): void {
    this._validateToken(token)
    if (!this._isActive) return

    this._isActive = false
    this._deactivateInternal()
  }

  private _validateToken(token: ActivationToken): void {
    if (token !== this._activationToken) {
      throw new Error('[CadPlugin] 非法的 ActivationToken，操作被拒绝')
    }
  }

  private _activateInternal(): void {
    // 注册 View-local InputConsumer，避免非 active view 的 hover/move 串入 CAD 交互态
    const viewEventRouter = this.context.getService('ViewEventRouter') as ViewEventRouter
    this._inputConsumer = this._createInputConsumer()
    viewEventRouter.addConsumer(this._inputConsumer)

    // 启动 CADController
    const controller = this.cadContainer.get<CADController>(CAD_TYPES.CADController)
    controller.start()
    this.markVisualActivity('cad.controllerStarted')
  }

  private _deactivateInternal(): void {
    try {
      const gridHelper = this.cadContainer.get<GridHelper>(CAD_TYPES.GridHelper)
      gridHelper.setEnabled(false)
    } catch {
      /* GridHelper may be unavailable during teardown. */
    }

    // 退出 CADController
    try {
      const controller = this.cadContainer.get<CADController>(CAD_TYPES.CADController)
      controller.exit()
      this.markVisualActivity('cad.controllerExited')
    } catch {
      /* 容器可能已清理 */
    }

    // 移除 InputConsumer
    if (this._inputConsumer) {
      try {
        const viewEventRouter = this.context.getService('ViewEventRouter') as ViewEventRouter
        viewEventRouter.removeConsumer(this._inputConsumer)
      } catch {
        /* ViewEventRouter 可能已销毁 */
      }
      this._inputConsumer = null
    }
  }

  // ─── InputConsumer 工厂 ────────────────────────────────

  /** 创建将 InputRouter 事件桥接到 CADController 的 InputConsumer */
  private _createInputConsumer(): ViewInputConsumer {
    const getController = () => this.cadContainer.get<CADController>(CAD_TYPES.CADController)

    /** 将 screen（clientX/clientY）转为画布局部坐标 */
    const getActiveViewId = () =>
      (this.context.getService('ViewRegistry') as ViewRegistry).getActive()?.id ?? null
    const isActiveViewEvent = (viewId: string) =>
      getActiveViewId() === viewId && this._isCadRenderEnabledForView(viewId)
    const toCanvasScreen = (event: ViewPointerEvent) => ({
      x: event.screen.x,
      y: event.screen.y,
    })

    return {
      name: 'cad-controller',
      priority: CAD_INPUT_PRIORITY,

      onPointerDown: (event: ViewPointerEvent): boolean => {
        if (!isActiveViewEvent(event.viewId)) {
          return false
        }
        const ctrl = getController()
        const screenPos = toCanvasScreen(event)
        ctrl.handlePointerDown(event.domEvent as unknown as MouseEvent, screenPos)
        return false // 不消费 down，等 up 时判定
      },

      onPointerMove: (event: ViewPointerEvent): boolean => {
        if (!isActiveViewEvent(event.viewId)) {
          return false
        }
        const ctrl = getController()
        const screenPos = toCanvasScreen(event)
        ctrl.handlePointerMove(event.domEvent as unknown as MouseEvent, screenPos)
        return false // move 不消费，允许相机交互
      },

      onPointerUp: (event: ViewPointerEvent): boolean => {
        if (!isActiveViewEvent(event.viewId)) {
          return false
        }
        const ctrl = getController()
        const screenPos = toCanvasScreen(event)
        return ctrl.handlePointerUp(event.domEvent as unknown as MouseEvent, screenPos)
      },

      onWinKeyDown: (event: ViewKeyEvent): boolean => {
        const ctrl = getController()
        ctrl.handleKeyDown(event.domEvent)
        // Esc 键消费（退出工具），其他键不消费
        return event.key === 'Escape'
      },

      onWinKeyUp: (event: ViewKeyEvent): boolean => {
        const ctrl = getController()
        ctrl.handleKeyUp(event.domEvent)
        return false
      },
    }
  }

  // ─── AssetPlugin 接口 ────────────────────────────────────

  /**
   * 加载 CAD 资产（单资产模式：同一时间仅允许一个资产）。
   *
   * @param request - CAD 加载请求，必须提供 data 或 url
   * @returns 加载完成的 CAD 资产句柄
   */
  async load(request: CadLoadRequest): Promise<CadHandle> {
    if (this.disposed) {
      throw new DOMException('Load aborted', 'AbortError')
    }

    // 运行时参数校验：data 和 url 至少提供一个
    if (!request.data && !request.url) {
      throw new Error('[CadPlugin] load 失败：request.data 和 request.url 至少需要提供一个')
    }

    const id = request.id ?? generateId()
    const controller = new AbortController()
    this.pendingLoads.set(id, controller)
    const loadStart = getLoggerManager().now()

    // 外部 signal 联动：先检查已中止，再用 once 监听并在 finally 中清理
    let abortHandler: (() => void) | undefined
    if (request.signal) {
      if (request.signal.aborted) {
        controller.abort()
      } else {
        abortHandler = () => controller.abort()
        request.signal.addEventListener('abort', abortHandler, { once: true })
      }
    }

    this.context.events.emit('asset.loading', { id, kind: this.kind })
    log.debug(
      `[CadPlugin] load started, id=${id}, format=${request.format ?? 'auto'}, source=${request.url ?? 'inline-data'}`
    )

    try {
      // 前置检查：signal 已中止时直接抛出，避免进入 loader
      if (controller.signal.aborted) {
        log.debug(`[CadPlugin] load aborted before loader, id=${id}`)
        throw new DOMException('Aborted', 'AbortError')
      }

      const result = await this.loadWithRegisteredLoader(id, request, controller.signal)
      result.objectManager.setStaticBatchingEnabled?.(this.staticBatchingEnabled)

      // 迟到结果丢弃：插件已销毁或加载已中止
      if (this.disposed || controller.signal.aborted) {
        throw new DOMException('Load aborted', 'AbortError')
      }

      const handle = this.createHandle(id, result, request)
      this.context.events.emit('asset.loaded', { id, kind: this.kind })
      const stats = collectCadEntityStats(result.entities as IEntity[])
      diagnosticsLog.info(
        `[CadPlugin] load completed, id=${id}, ${formatCadEntityStats(stats)}, layers=${result.dataManager.layers.length}, elapsed=${(getLoggerManager().now() - loadStart).toFixed(1)}ms`
      )
      log.debug(
        `[CadPlugin] load completed, id=${id}, entities=${result.entities.length}, layers=${result.dataManager.layers.length}, elapsed=${(getLoggerManager().now() - loadStart).toFixed(1)}ms`
      )

      // 首个加载的资产自动成为活跃会话
      if (this.activeSessionId === null) {
        this.setActive(id)
      }

      return handle
    } catch (err) {
      // 非中止错误时发送 asset.error 事件
      if (!(err instanceof DOMException && err.name === 'AbortError')) {
        log.error(
          `[CadPlugin] load failed, id=${id}, source=${request.url ?? 'inline-data'}, reason=${
            err instanceof Error ? err.message : String(err)
          }`
        )
        this.context.events.emit('asset.error', {
          id,
          kind: this.kind,
          error: err instanceof Error ? err : new Error(String(err)),
        })
      } else {
        log.debug(`[CadPlugin] load aborted, id=${id}, source=${request.url ?? 'inline-data'}`)
      }
      throw err
    } finally {
      // 清理外部 signal 监听
      if (request.signal && abortHandler) {
        request.signal.removeEventListener('abort', abortHandler)
      }
      this.pendingLoads.delete(id)
      log.debug(`[CadPlugin] load settled, id=${id}, pendingLoads=${this.pendingLoads.size}`)
    }
  }

  /**
   * 挂载资产到场景图：将 CAD 对象添加到 cadRoot，并同步初始可见性。
   *
   * @param handle - 已加载的 CAD 资产句柄
   */
  mount(handle: CadHandle): void {
    const obj = handle.object3D
    if (!obj) {
      log.warn(`[CadPlugin] mount 跳过：资产 "${handle.id}" 无 object3D`)
      return
    }
    if (obj instanceof THREE.Object3D) {
      setMaskRecursive(obj, obj.layers.mask)
    }
    // 同步初始可见性到 object3D
    obj.visible = handle.visible
    const root = this.context.sceneGraph.cadRoot
    root.add(obj)
    this.context.sceneBounds?.register({
      id: handle.id,
      kind: this.kind,
      object3D: obj,
      getLocalBoundingBox: () => handle.boundingBox,
      isVisible: () => handle.visible,
    })
    this.context.events.emit('asset.mounted', { id: handle.id, kind: this.kind })
    const stats = collectCadEntityStats(getCadHandleEntities(handle))
    diagnosticsLog.info(`[CadPlugin] mounted, id=${handle.id}, ${formatCadEntityStats(stats)}`)
    log.debug(
      `[CadPlugin] mounted, id=${handle.id}, entities=${handle.dataManager.entities.length}`
    )
  }

  /**
   * 从场景图卸载资产：移除 3D 对象。
   *
   * @param id - 资产 id
   */
  unmount(id: AssetId): void {
    const handle = this.assets.get(id)
    if (!handle) {
      log.warn(`[CadPlugin] unmount 跳过：未找到资产 "${id}"`)
      return
    }

    this.context.sceneBounds?.unregister(this.kind, id)
    // 从场景图移除 3D 对象
    const obj = handle.object3D
    if (obj?.parent) {
      obj.parent.remove(obj)
    }

    this.context.events.emit('asset.unmounted', { id, kind: this.kind })
    log.debug(`[CadPlugin] unmounted, id=${id}, assets=${this.assets.size}`)
  }

  /**
   * 按 ID 获取资产句柄。
   *
   * @param id - 资产 id
   * @returns 资产句柄；未找到时返回 undefined
   */
  getAsset(id: AssetId): CadHandle | undefined {
    return this.assets.get(id)
  }

  /**
   * 获取所有已加载资产。
   *
   * @returns 当前插件托管的全部资产句柄
   */
  getAll(): CadHandle[] {
    return Array.from(this.assets.values())
  }

  /**
   * 设置资产可见性，同步更新 object3D.visible。
   *
   * @param id - 资产 id
   * @param visible - 是否可见
   */
  setVisibility(id: AssetId, visible: boolean): void {
    const handle = this.assets.get(id)
    if (handle) {
      handle.visible = visible
      // 同步 object3D.visible，确保场景图中渲染状态一致
      if (handle.object3D) {
        handle.object3D.visible = visible
      }
      this.context.events.emit('asset.visibilityChanged', { id, visible })
      log.debug(`[CadPlugin] visibility changed, id=${id}, visible=${visible}`)
    }
  }

  /**
   * 渲染指定阶段：MainOpaque 阶段渲染 CAD 实体，Overlay3D 阶段渲染辅助叠加。
   *
   * @param frame - 视图帧上下文
   * @param phase - 当前渲染阶段
   * @param ctx - 阶段渲染上下文，提供 renderer 与输出目标
   */
  render(frame: ViewFrameContext, phase: RenderPhase, ctx: PhaseRenderContext): void {
    if (phase === RenderPhase.MainOpaque) {
      if (!this._isCadRenderEnabledForView(frame.viewId)) {
        return
      }
      // 仅在有 CAD 资产时才调用 Three.js 渲染，避免无 CAD 时产生额外提交。
      const hasVisibleCad = Array.from(this.assets.values()).some(
        (handle) => handle.visible && handle.object3D?.visible !== false
      )
      if (!hasVisibleCad) return

      for (const handle of this.assets.values()) {
        if (handle.visible && handle.object3D?.visible !== false) {
          handle.objectManager?.flushPendingUpdates?.()
        }
      }

      // WebGPU 走受控 scene 提交（viewport/scissor/target 由 backend 依当前 View 决定）；
      // WebGL2 保持既有 legacy 路径（resetState + RT 进出协议 + renderer.info 诊断）。
      const backendKind = (ctx.graphics as { kind?: string } | undefined)?.kind ?? 'webgl2'
      const usesLegacyRenderer = backendKind === 'webgl2'
      const renderer = usesLegacyRenderer ? ctx.renderer : null
      const cadRoot = this.context.sceneGraph.cadRoot
      const cadRenderScene = (
        this.context.sceneGraph as typeof this.context.sceneGraph & {
          cadRenderScene?: THREE.Scene
        }
      ).cadRenderScene

      let visibleEntities = 0
      let diagnosticsFrame: ReturnType<CadPerformanceDiagnostics['beginFrame']> = null
      let batchCount = 0
      let materialBatchCount = 0
      if (this.performanceDiagnostics.isEnabled()) {
        startCadDashScaleTracking()
        const visibleHandles = Array.from(this.assets.values()).filter(
          (handle) => handle.visible && handle.object3D?.visible !== false
        )
        visibleEntities = visibleHandles.reduce(
          (total, handle) => total + getCadHandleEntities(handle).length,
          0
        )
        const visibleTextEntities = visibleHandles.reduce(
          (total, handle) =>
            total +
            getCadHandleEntities(handle).filter((entity) =>
              [
                EntityType.MText,
                EntityType.AlignDimension,
                EntityType.AngularDimension,
                EntityType.MLeader,
              ].includes(entity.type)
            ).length,
          0
        )
        const renderableTypes: Record<string, { renderables: number; drawCalls: number }> = {}
        let renderables = 0
        const materialBatches: Record<string, number> = {}
        const textObjects = { total: 0, visible: 0, outlines: 0 }
        for (const handle of visibleHandles) {
          if (!handle.object3D) continue
          const renderableStats = collectVisibleCadRenderables(handle.object3D)
          renderables += renderableStats.count
          batchCount += renderableStats.batchCount
          textObjects.total += renderableStats.textObjects.total
          textObjects.visible += renderableStats.textObjects.visible
          textObjects.outlines += renderableStats.textObjects.outlines
          for (const [key, count] of Object.entries(renderableStats.materialBatches)) {
            materialBatches[key] = (materialBatches[key] ?? 0) + count
          }
          for (const [type, stats] of Object.entries(renderableStats.byEntityType)) {
            const current = renderableTypes[type] ?? { renderables: 0, drawCalls: 0 }
            current.renderables += stats.renderables
            current.drawCalls += stats.drawCalls
            renderableTypes[type] = current
          }
        }
        materialBatchCount = Object.keys(materialBatches).length
        diagnosticsFrame = this.performanceDiagnostics.beginFrame({
          frameNumber: frame.frameNumber,
          viewId: frame.viewId,
          phase,
          visibleAssets: visibleHandles.length,
          visibleEntities,
          visibleTextEntities,
          renderables,
          renderableTypes,
          materialBatches,
          textObjects,
          entityStats: visibleHandles.map((handle) =>
            collectCadEntityStats(getCadHandleEntities(handle))
          ),
        })
      }

      // 恢复 Three.js 内部状态（PotreeRenderer 等使用原始 WebGL 调用后需要同步）
      const resetStartedAt = diagnosticsFrame ? getLoggerManager().now() : 0
      if (renderer) {
        renderer.resetState()
      }
      const resetStateMs = diagnosticsFrame ? getLoggerManager().now() - resetStartedAt : 0

      // RT 进出协议：保存当前 renderTarget → 切换 → 渲染 → 恢复
      const savedTarget = renderer ? renderer.getRenderTarget() : null
      const savedViewport =
        renderer && typeof renderer.getViewport === 'function'
          ? renderer.getViewport(new THREE.Vector4())
          : null
      const savedScissor =
        renderer && typeof renderer.getScissor === 'function'
          ? renderer.getScissor(new THREE.Vector4())
          : null
      const savedScissorTest =
        renderer && typeof renderer.getScissorTest === 'function'
          ? renderer.getScissorTest()
          : false
      if (renderer) {
        renderer.setRenderTarget(ctx.outputTarget)
        renderer.setViewport(
          ctx.viewport.x,
          ctx.viewport.y,
          ctx.viewport.width,
          ctx.viewport.height
        )
        renderer.setScissor(ctx.viewport.x, ctx.viewport.y, ctx.viewport.width, ctx.viewport.height)
        renderer.setScissorTest(true)
      }
      // 新版 SceneGraphService 提供稳定 CAD 场景；旧 harness 没有该字段时保留兼容回退。
      const renderScene = cadRenderScene ?? this.isolatedCadScene
      const restoreCadRootParent = cadRenderScene
        ? () => {}
        : this._attachCadRootToIsolatedScene(cadRoot)
      const rendererInfo = renderer
        ? (
            renderer as THREE.WebGLRenderer & {
              info?: { autoReset?: boolean; render?: { calls?: number } }
            }
          ).info
        : undefined
      const callsBeforeRender = rendererInfo?.render?.calls ?? 0
      const renderStartedAt = diagnosticsFrame ? getLoggerManager().now() : 0
      try {
        if (renderer) {
          renderer.render(renderScene, frame.camera)
        } else {
          submitSceneInView(ctx, renderScene, frame.camera)
        }
      } finally {
        const sceneRenderMs = diagnosticsFrame ? getLoggerManager().now() - renderStartedAt : 0
        restoreCadRootParent()
        if (renderer && savedViewport) {
          renderer.setViewport(savedViewport.x, savedViewport.y, savedViewport.z, savedViewport.w)
        }
        if (renderer && savedScissor) {
          renderer.setScissor(savedScissor.x, savedScissor.y, savedScissor.z, savedScissor.w)
        }
        if (renderer) {
          renderer.setScissorTest(savedScissorTest)
          renderer.setRenderTarget(savedTarget)
        }
        const callsAfterRender = rendererInfo?.render?.calls ?? 0
        const drawCalls =
          rendererInfo?.autoReset === false
            ? Math.max(0, callsAfterRender - callsBeforeRender)
            : callsAfterRender
        this.performanceDiagnostics.finish(diagnosticsFrame, {
          drawCalls,
          resetStateMs,
          sceneRenderMs,
          dashScaleMs: stopCadDashScaleTracking(),
          batchCount,
          materialBatchCount,
        })
      }
    }
    // Overlay3D 阶段暂为空实现，后续由 CAD 辅助工具填充
  }

  /** 返回最近一次已启用的 CAD 帧性能快照；诊断未开启或尚未渲染时返回 null。 */
  getPerformanceSnapshot() {
    return this.performanceDiagnostics.getLastSnapshot()
  }

  /** 拾取查询：遍历所有可见 CAD 资产，返回距离最近的命中 */
  private _attachCadRootToIsolatedScene(cadRoot: THREE.Object3D): () => void {
    const originalParent = cadRoot.parent
    const originalIndex = originalParent?.children.indexOf(cadRoot) ?? -1

    if (originalParent) {
      originalParent.remove(cadRoot)
    }
    this.isolatedCadScene.add(cadRoot)

    return () => {
      this.isolatedCadScene.remove(cadRoot)
      if (!originalParent) {
        return
      }

      originalParent.add(cadRoot)
      const restoredIndex = originalParent.children.indexOf(cadRoot)
      if (originalIndex >= 0 && restoredIndex >= 0 && restoredIndex !== originalIndex) {
        originalParent.children.splice(restoredIndex, 1)
        originalParent.children.splice(
          Math.min(originalIndex, originalParent.children.length),
          0,
          cadRoot
        )
      }
    }
  }

  /**
   * 拾取查询：遍历所有可见 CAD 资产，返回距离最近的命中。
   *
   * @param query - 拾取查询参数
   * @returns 最近一次命中结果；未命中时返回 null
   */
  pick(query: PickQuery): PickResult | null {
    let best: PickResult | null = null

    for (const [assetId, handle] of this.assets) {
      // 跳过不可见的资产
      if (!handle.visible) continue

      try {
        const hits = this.withBoundAssetView(assetId, () => {
          const detector = this.cadContainer.get<ICadEntityDetector>(CAD_TYPES.EntityDetector)
          if (!detector || typeof detector.detectWithHit !== 'function') {
            return []
          }
          return detector.detectWithHit(
            { x: query.ndc.x, y: query.ndc.y },
            {
              camera: query.camera,
              viewport: query.viewport,
              layerMask: query.layerMask,
            }
          )
        })
        if (hits.length > 0) {
          const hit = hits[0]
          const result: PickResult = {
            assetId,
            kind: this.kind,
            point: new THREE.Vector3(hit.point.x, hit.point.y, hit.point.z),
            distance: hit.distance,
            entityId: hit.id,
          }
          if (!best || result.distance < best.distance) {
            best = result
          }
        }
      } catch {
        // EntityDetector 未绑定或调用异常，跳过该资产
      }
    }

    return best
  }

  /**
   * 释放资产。先 unmount 再清理。不传 id 则释放全部。
   *
   * @param id - 资产 id；缺省释放全部
   */
  dispose(id?: AssetId): void {
    if (id !== undefined) {
      const handle = this.assets.get(id)
      const pending = this.pendingLoads.get(id)
      if (pending) {
        pending.abort()
        this.pendingLoads.delete(id)
        log.debug(`[CadPlugin] dispose aborted pending load, id=${id}`)
      }
      // 先从场景图移除，再删除资产；只有资产确实存在时才发事件
      const beforeStats = handle ? collectCadEntityStats(getCadHandleEntities(handle)) : null
      if (beforeStats) {
        diagnosticsLog.info(
          `[CadPlugin] dispose started, id=${id}, ${formatCadEntityStats(beforeStats)}`
        )
      }
      this.unmount(id)
      ;(handle?.objectManager as { dispose?: () => void } | undefined)?.dispose?.()
      const existed = this.assets.delete(id)

      // 更新 activeSessionId 并发送 activeChanged 事件
      if (this.activeSessionId === id) {
        const previousId = id
        const remaining = Array.from(this.assets.keys())
        this.activeSessionId = remaining.length > 0 ? remaining[0] : null
        this.bindActiveHandleToView()
        this.context.events.emit('cad.activeChanged', {
          assetId: this.activeSessionId,
          previousId,
        })
      }

      if (existed) {
        this.context.events.emit('asset.disposed', { id, kind: this.kind })
        diagnosticsLog.info(`[CadPlugin] disposed, id=${id}, assets=${this.assets.size}`)
      }
    } else {
      // 批量释放：逐个 unmount 并对每个实际存在的资产发事件
      let totalEntities = 0
      let totalTextLike = 0
      for (const handle of this.assets.values()) {
        const stats = collectCadEntityStats(getCadHandleEntities(handle))
        totalEntities += stats.total
        totalTextLike += stats.textLike
      }
      if (this.assets.size > 0) {
        diagnosticsLog.info(
          `[CadPlugin] dispose all started, assets=${this.assets.size}, entities=${totalEntities}, textLike=${totalTextLike}`
        )
      }
      for (const assetId of Array.from(this.assets.keys())) {
        const handle = this.assets.get(assetId)
        this.unmount(assetId)
        ;(handle?.objectManager as { dispose?: () => void } | undefined)?.dispose?.()
        this.context.events.emit('asset.disposed', { id: assetId, kind: this.kind })
      }
      this.assets.clear()
      diagnosticsLog.info('[CadPlugin] disposed all assets')

      // 补发 activeChanged 事件
      if (this.activeSessionId !== null) {
        const previousId = this.activeSessionId
        this.activeSessionId = null
        this.context.events.emit('cad.activeChanged', { assetId: null, previousId })
      }
    }
  }

  // ─── ExportableAssetPlugin 接口 ──────────────────────────

  /**
   * 导出资产为指定格式。
   *
   * @param id - 资产 id
   * @param format - 目标格式，如 'dxf'
   * @param options - 导出选项，由具体导出器定义
   * @returns 导出结果 Blob
   */
  async export(id: AssetId, format: string, options?: ExportOptions): Promise<Blob> {
    const handle = this.assets.get(id)
    if (!handle) {
      throw new Error(`[CadPlugin] export 失败：未找到资产 "${id}"`)
    }

    const exporter = this.exporters.get(format)
    if (!exporter) {
      throw new FormatNotSupportedError(`(export:${id})`, this.exporters.formats())
    }

    const scope = new DisposableScope()
    const exportStart = getLoggerManager().now()
    try {
      this.context.events.emit('asset.exportStarted', { id, kind: this.kind, format })
      log.debug(`[CadPlugin] export started, id=${id}, format=${format}`)
      const blob = await this.withBoundAssetViewAsync(id, () => exporter.export(handle, options))
      this.context.events.emit('asset.exportCompleted', { id, kind: this.kind, format })
      log.debug(
        `[CadPlugin] export completed, id=${id}, format=${format}, bytes=${blob.size}, elapsed=${(getLoggerManager().now() - exportStart).toFixed(1)}ms`
      )
      return blob
    } catch (err) {
      log.error(
        `[CadPlugin] export failed, id=${id}, format=${format}, reason=${
          err instanceof Error ? err.message : String(err)
        }`
      )
      this.context.events.emit('asset.exportError', {
        id,
        kind: this.kind,
        format,
        error: err instanceof Error ? err : new Error(String(err)),
      })
      throw err
    } finally {
      scope.dispose()
    }
  }

  // ─── Editable 接口 ──────────────────────────────────────

  /** 撤销上一步操作 */
  undo(): void {
    if (!this.getActive()) return
    try {
      this.cadContainer.get<ICadHistoryManager>(CAD_TYPES.HistoryManager).undo()
    } catch {
      log.warn('[CadPlugin] undo 失败：HistoryManager 不可用')
    }
  }

  /** 重做上一步撤销 */
  redo(): void {
    if (!this.getActive()) return
    try {
      this.cadContainer.get<ICadHistoryManager>(CAD_TYPES.HistoryManager).redo()
    } catch {
      log.warn('[CadPlugin] redo 失败：HistoryManager 不可用')
    }
  }

  /** 执行编辑命令（HistoryManager 暂无 executeCommand 实现，预留接口） */
  executeCommand(_command: string, _params?: Record<string, unknown>): void {
    // HistoryManager 当前不支持按名称执行命令，此方法为 Editable 接口预留
  }

  /** 是否可撤销 */
  get canUndo(): boolean {
    if (!this.getActive()) return false
    try {
      return this.cadContainer.get<ICadHistoryManager>(CAD_TYPES.HistoryManager).canUndo
    } catch {
      return false
    }
  }

  /** 是否可重做 */
  get canRedo(): boolean {
    if (!this.getActive()) return false
    try {
      return this.cadContainer.get<ICadHistoryManager>(CAD_TYPES.HistoryManager).canRedo
    } catch {
      return false
    }
  }

  // ─── Selectable 接口 ────────────────────────────────────

  /**
   * 获取当前选中的实体 ID 列表。
   *
   * @returns 当前选中实体 id 列表；未激活时返回空数组
   */
  getSelectedEntityIds(): string[] {
    if (!this.getActive()) return []
    try {
      const selector = this.cadContainer.get<ICadEntitySelector>(CAD_TYPES.EntitySelector)
      return selector.getSelected().map((e) => e.id)
    } catch {
      return []
    }
  }

  /**
   * 从外部 SelectionService 同步选择状态到 CAD 内部。
   *
   * @param entityIds - 目标实体 id 列表；空数组表示清空选择
   */
  syncSelectionFromService(entityIds: string[]): void {
    if (!this.getActive()) return
    try {
      const selector = this.cadContainer.get<ICadEntitySelector>(CAD_TYPES.EntitySelector)
      const viewManager = this.cadContainer.get<ICadViewManager>(CAD_TYPES.CADViewManager)

      // 清空当前选择
      selector.clearSelection()

      if (entityIds.length === 0) return

      // 通过 CADViewManager 将 entityId 解析为实体对象，再调用 select
      const entities = entityIds
        .map((id: string) => viewManager.getEntity(id))
        .filter(Boolean) as Array<{ id: string }>

      if (entities.length > 0) {
        selector.select(entities, { mode: 'replace' as never })
      }
    } catch {
      log.warn('[CadPlugin] syncSelectionFromService 失败：EntitySelector 或 CADViewManager 不可用')
    }
  }

  // ─── 工具调度 ─────────────────────────────────────────

  /** 从 cadContainer 解析绘制工具 */
  private _resolveDrawTool(toolType: DrawToolType): BaseTool | null {
    const symbolMap: Partial<Record<DrawToolType, symbol>> = {
      [DrawToolType.Polyline]: CAD_TYPES.DrawPolylineTool,
      [DrawToolType.Arc]: CAD_TYPES.DrawArcTool,
      [DrawToolType.Circle]: CAD_TYPES.DrawCircleTool,
      [DrawToolType.Rect]: CAD_TYPES.DrawRectTool,
      [DrawToolType.Spline]: CAD_TYPES.DrawSplineTool,
      [DrawToolType.Text]: CAD_TYPES.DrawTextTool,
      [DrawToolType.Dimension]: CAD_TYPES.DrawDimensionTool,
      [DrawToolType.Polygon]: CAD_TYPES.DrawPolygonTool,
      [DrawToolType.Ellipse]: CAD_TYPES.DrawEllipseTool,
      [DrawToolType.Ray]: CAD_TYPES.DrawRayTool,
      [DrawToolType.Hatch]: CAD_TYPES.DrawHatchTool,
      [DrawToolType.MLeader]: CAD_TYPES.DrawMLeaderTool,
      [DrawToolType.AngularDimension]: CAD_TYPES.DrawAngularDimensionTool,
    }
    const sym = symbolMap[toolType]
    if (!sym) {
      log.warn(`[CadPlugin] 不支持的绘制工具类型: "${toolType}"`)
      return null
    }
    try {
      return this.cadContainer.get<BaseTool>(sym)
    } catch {
      log.warn(`[CadPlugin] 解析绘制工具失败: "${toolType}"`)
      return null
    }
  }

  /** 从 cadContainer 解析编辑工具 */
  private _resolveEditTool(toolType: EditToolType): BaseTool | null {
    const symbolMap: Partial<Record<EditToolType, symbol>> = {
      [EditToolType.Join]: CAD_TYPES.JoinEntityTool,
      [EditToolType.Copy]: CAD_TYPES.CopyEntityTool,
      [EditToolType.Move]: CAD_TYPES.MoveEntityTool,
      [EditToolType.Close]: CAD_TYPES.CloseEntityTool,
      [EditToolType.Rotate]: CAD_TYPES.RotateEntityTool,
      [EditToolType.Offset]: CAD_TYPES.OffsetEntityTool,
      [EditToolType.Break]: CAD_TYPES.BreakEntityTool,
      [EditToolType.Trim]: CAD_TYPES.TrimEntityTool,
      [EditToolType.Extend]: CAD_TYPES.ExtendEntityTool,
    }
    const sym = symbolMap[toolType]
    if (!sym) {
      log.warn(`[CadPlugin] 不支持的编辑工具类型: "${toolType}"`)
      return null
    }
    try {
      return this.cadContainer.get<BaseTool>(sym)
    } catch {
      log.warn(`[CadPlugin] 解析编辑工具失败: "${toolType}"`)
      return null
    }
  }

  /**
   * 激活绘制工具。
   *
   * @param params - 绘制工具参数，包含工具类型与初始数据
   */
  activateDrawTool(params: IDrawToolParams): void {
    if (!this.getActive()) return
    const { toolType, entityData } = params
    const controller =
      params.controller ?? this.cadContainer.get<CADController>(CAD_TYPES.CADController)
    const drawTool = this._resolveDrawTool(toolType)
    drawTool?.start(controller, StartMode.alone, entityData, (params as { mode?: unknown }).mode)
  }

  /**
   * 退出绘制工具。
   *
   * @param toolType - 要退出的绘制工具类型
   */
  exitDrawTool(toolType: DrawToolType): void {
    const drawTool = this._resolveDrawTool(toolType)
    drawTool?.exit()
  }

  /**
   * 激活编辑工具。
   *
   * @param params - 编辑工具参数，包含工具类型
   */
  activateEditTool(params: IEditToolParams): void {
    if (!this.getActive()) return
    const { toolType } = params
    const controller =
      params.controller ?? this.cadContainer.get<CADController>(CAD_TYPES.CADController)
    const editTool = this._resolveEditTool(toolType)
    editTool?.start(controller, StartMode.alone)
  }

  /**
   * 退出编辑工具。
   *
   * @param toolType - 要退出的编辑工具类型
   */
  exitEditTool(toolType: EditToolType): void {
    const editTool = this._resolveEditTool(toolType)
    editTool?.exit()
  }

  /**
   * 设置正交的启用状态。
   *
   * @param enabled - 是否启用正交
   */
  setOrthogonalEnabled(enabled: boolean) {
    const orthogonalHelper = this.cadContainer.get<OrthogonalHelper>(CAD_TYPES.OrthogonalHelper)
    orthogonalHelper.setEnabled(enabled)
  }

  /**
   * 获取正交的启用状态。
   *
   * @returns 是否启用正交
   */
  isOrthogonalEnabled(): boolean {
    const orthogonalHelper = this.cadContainer.get<OrthogonalHelper>(CAD_TYPES.OrthogonalHelper)
    return orthogonalHelper.isEnabled()
  }

  /**
   * 设置捕捉的启用状态。
   *
   * @param enabled - 是否启用捕捉
   */
  setCaptureEnabled(enabled: boolean) {
    const captureHelper = this.cadContainer.get<CaptureHelper>(CAD_TYPES.CaptureHelper)
    captureHelper.setEnabled(enabled)
  }

  /**
   * 获取捕捉的启用状态。
   *
   * @returns 是否启用捕捉
   */
  isCaptureEnabled(): boolean {
    const captureHelper = this.cadContainer.get<CaptureHelper>(CAD_TYPES.CaptureHelper)
    return captureHelper.isEnabled()
  }

  /**
   * 设置指定捕捉类型的启用状态。
   *
   * @param type - 捕捉类型
   * @param enabled - 是否启用该类型
   */
  setCaptureType(type: CaptureType, enabled: boolean) {
    const captureHelper = this.cadContainer.get<CaptureHelper>(CAD_TYPES.CaptureHelper)
    captureHelper.setCaptureType(type, enabled)
  }

  /**
   * 局部更新捕捉配置。
   *
   * @param config - 要合并的捕捉配置字段
   */
  setCaptureConfig(config: Partial<ICaptureConfig>) {
    const captureHelper = this.cadContainer.get<CaptureHelper>(CAD_TYPES.CaptureHelper)
    captureHelper.setConfig(config)
  }

  /**
   * 设置网格的启用状态
   * @param enabled 是否启用背景网格
   */
  setGridEnabled(enabled: boolean) {
    const gridHelper = this.cadContainer.get<GridHelper>(CAD_TYPES.GridHelper)
    gridHelper.setEnabled(enabled)
  }

  /**
   * 获取网格的启用状态
   * @returns 是否启用背景网格
   */
  isGridEnabled(): boolean {
    const gridHelper = this.cadContainer.get<GridHelper>(CAD_TYPES.GridHelper)
    return gridHelper.isEnabled()
  }

  setPolarEnabled(enabled: boolean) {
    const polarHelper = this.cadContainer.get<PolarHelper>(CAD_TYPES.PolarHelper)
    polarHelper.setEnabled(enabled)
  }

  /**
   * 设置极轴角度列表。
   *
   * @param angles - 启用的极轴角度数组，单位度
   */
  setPolarAngles(angles: number[]) {
    const polarHelper = this.cadContainer.get<PolarHelper>(CAD_TYPES.PolarHelper)
    polarHelper.setPolarAngles(angles)
  }

  /**
   * 获取当前启用的极轴角度列表。
   *
   * @returns 极轴角度数组，单位度
   */
  getPolarAngles(): number[] {
    const polarHelper = this.cadContainer.get<PolarHelper>(CAD_TYPES.PolarHelper)
    return polarHelper.polarAngles
  }

  /**
   * 获取极轴是否处于启用状态。
   *
   * @returns 极轴是否启用
   */
  isPolarEnabled(): boolean {
    const polarHelper = this.cadContainer.get<PolarHelper>(CAD_TYPES.PolarHelper)
    return polarHelper.isEnabled()
  }

  /**
   * 设置附着点云的启用状态。
   *
   * @param enabled - 是否启用点云附着
   */
  setPointCloudEnabled(enabled: boolean) {
    const pointCloudHelper = this.cadContainer.get<PointCloudHelper>(CAD_TYPES.PointCloudHelper)
    pointCloudHelper.setEnabled(enabled)
  }

  /**
   * 获取附着点云的启用状态。
   *
   * @returns 点云附着是否启用
   */
  isPointCloudEnabled(): boolean {
    const pointCloudHelper = this.cadContainer.get<PointCloudHelper>(CAD_TYPES.PointCloudHelper)
    return pointCloudHelper.isEnabled()
  }

  // ─── 私有方法 ───────────────────────────────────────────

  /** 应用裁切体变更到 CAD 渲染 */
  private _applyClipVolumes(volumes: ReadonlyArray<ClipVolume>): void {
    const globalVolumes = volumes.filter((volume) => volume.viewId == null)
    try {
      const viewManager = this.cadContainer.get<ICadViewManager>(CAD_TYPES.CADViewManager)
      if (viewManager && typeof viewManager.setClipVolumes === 'function') {
        viewManager.setClipVolumes(globalVolumes)
        this.markVisualActivity('cad.clipVolumes')
      }
    } catch {
      // CADViewManager 不可用，静默忽略
    }
  }

  private markVisualActivity(reason: string): void {
    this.context?.renderInvalidation?.invalidate(reason)
  }

  private _isCadRenderEnabledForView(viewId: string): boolean {
    if (!this.context.hasService('ViewRegistry')) {
      return true
    }
    try {
      const viewRegistry = this.context.getService('ViewRegistry') as ViewRegistry
      const view = viewRegistry.get(viewId)
      return view?.userData?.renderCad !== false
    } catch {
      return true
    }
  }

  private async loadWithRegisteredLoader(
    id: AssetId,
    request: CadLoadRequest,
    signal: AbortSignal
  ): Promise<CadLoadResult> {
    const loader = request.format ? this.loaders.get(request.format) : this.loaders.detect(request)

    if (!loader) {
      throw new FormatNotSupportedError(request.url ?? '(inline-data)', this.loaders.formats())
    }
    log.debug(
      `[CadPlugin] loader selected, id=${id}, format=${loader.format}, source=${request.url ?? 'inline-data'}`
    )

    const abortRacePromise = new Promise<never>((_, reject) => {
      signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), {
        once: true,
      })
    })

    const loaderPromise = loader.load(request, {
      signal,
      onProgress: (progress) =>
        this.context.events.emit('asset.progress', {
          id,
          kind: this.kind,
          progress,
          loaded: progress,
          total: 1,
        }),
    })

    return Promise.race([loaderPromise, abortRacePromise])
  }

  /** 根据加载结果创建资产句柄并存入 assets map */
  private createHandle(id: AssetId, result: CadLoadResult, request: CadLoadRequest): CadHandle {
    const plugin = this
    if (this.context.hasService('UnitPresentationService')) {
      result.objectManager.setUnitPresentationService?.(
        this.context.getService('UnitPresentationService')
      )
    }

    const withActiveHistory = (
      action: (viewManager: ICadViewManager, historyManager: ICadHistoryManager | null) => void
    ): void => {
      if (plugin.getActiveId() !== id) {
        plugin.runHandleDataAction(result, action)
        return
      }

      try {
        const viewManager = plugin.cadContainer.get<ICadViewManager>(CAD_TYPES.CADViewManager)
        let historyManager: ICadHistoryManager | null = null
        try {
          historyManager = plugin.cadContainer.get<ICadHistoryManager>(CAD_TYPES.HistoryManager)
        } catch {
          historyManager = null
        }
        action(viewManager, historyManager)
      } catch {
        plugin.runHandleDataAction(result, action)
      }
    }

    const handle: CadHandle = {
      id,
      kind: 'cad',
      name: request.name ?? request.url ?? '(unnamed)',
      visible: true,
      boundingBox: result.metadata.boundingBox,
      object3D: result.objectManager.getSceneGroup(),
      get layers(): CadLayer[] {
        return result.dataManager.layers
      },

      get selectedEntityIds(): string[] {
        return plugin.getSelectedEntityIds()
      },

      get canUndo(): boolean {
        return plugin.canUndo
      },

      get canRedo(): boolean {
        return plugin.canRedo
      },

      get dataManager(): DataManager {
        return result.dataManager
      },

      get objectManager(): ObjectManager {
        return result.objectManager
      },

      activateDrawTool(params: IDrawToolParams): void {
        plugin.ensureHandleIsActive(id)
        plugin.activateDrawTool(params)
      },

      exitDrawTool(toolType: DrawToolType): void {
        plugin.ensureHandleIsActive(id)
        plugin.exitDrawTool(toolType)
      },

      activateEditTool(params: IEditToolParams): void {
        plugin.ensureHandleIsActive(id)
        plugin.activateEditTool(params)
      },

      exitEditTool(toolType: EditToolType): void {
        plugin.ensureHandleIsActive(id)
        plugin.exitEditTool(toolType)
      },

      undo(): void {
        plugin.ensureHandleIsActive(id)
        plugin.undo()
      },

      redo(): void {
        plugin.ensureHandleIsActive(id)
        plugin.redo()
      },

      executeCommand(_name: string, _params?: Record<string, unknown>): void {
        // HistoryManager 当前不支持按名称执行命令，预留接口
      },

      serialize(): CadMetadata {
        return result.dataManager.serialize() as unknown as CadMetadata
      },

      replaceData(_data: CadMetadata): void {
        // 预留接口：需要 ViewManager 支持数据替换
      },

      addEntities(entities: CadEntityData[], options?: { recordHistory?: boolean }): void {
        const typedEntities = entities as unknown as IEntity[]
        withActiveHistory((viewManager, historyManager) => {
          if (options?.recordHistory && historyManager) {
            const command = new AddEntityCommand(
              typedEntities,
              viewManager as unknown as CADViewManager
            )
            historyManager.addCommandAndExecute(command)
            return
          }
          viewManager.addEntities(typedEntities)
        })
      },

      updateEntities(
        entityIds: string[],
        patch: Partial<CadEntityData>,
        options?: { recordHistory?: boolean; updateOptions?: IUpdateOptions }
      ): void {
        withActiveHistory((viewManager, historyManager) => {
          const typedEntities = entityIds
            .map((entityId) => viewManager.dataManager.getEntity(entityId))
            .filter((entity) => entity !== undefined) as IEntity[]
          if (typedEntities.length === 0) return
          if (options?.recordHistory && historyManager) {
            const command = new UpdateEntityCommand(
              typedEntities,
              patch,
              options?.updateOptions,
              viewManager as unknown as CADViewManager
            )
            historyManager.addCommandAndExecute(command)
            return
          }
          typedEntities.forEach((entity) => {
            Object.assign(entity, cloneDeep(patch))
          })
          viewManager.updateEntities(typedEntities, options?.updateOptions)
        })
      },

      deleteEntities(entityIds: string[], options?: { recordHistory?: boolean }): void {
        withActiveHistory((viewManager, historyManager) => {
          const typedEntities = entityIds
            .map((entityId) => viewManager.dataManager.getEntity(entityId))
            .filter((entity) => entity !== undefined) as IEntity[]
          if (typedEntities.length === 0) return
          if (options?.recordHistory && historyManager) {
            const command = new DeleteEntityCommand(
              typedEntities,
              viewManager as unknown as CADViewManager
            )
            historyManager.addCommandAndExecute(command)
            return
          }
          viewManager.deleteEntities(typedEntities)
        })
      },

      importDxf(
        dxfString: string,
        options?: { recordHistory?: boolean; mergeLayers?: boolean }
      ): void {
        try {
          const parsed = DxfParser.parse(dxfString)
          if (!parsed || !parsed.entities || parsed.entities.length === 0) return
          withActiveHistory((viewManager, historyManager) => {
            let entities = parsed.entities as unknown as IEntity[]
            if (parsed.layers.length > 0) {
              if (options?.mergeLayers === false) {
                viewManager.addLayers(parsed.layers)
              } else {
                const existingLayerByName = new Map(
                  viewManager.dataManager.layers.map((layer) => [layer.name, layer])
                )
                const importedLayerIdMap = new Map<string, string>()
                for (const layer of parsed.layers) {
                  const existing = existingLayerByName.get(layer.name)
                  if (existing) {
                    importedLayerIdMap.set(layer.id, existing.id)
                  } else {
                    viewManager.addLayer(layer)
                    existingLayerByName.set(layer.name, layer)
                  }
                }
                entities = entities.map((entity) => {
                  const mergedLayerId = importedLayerIdMap.get(entity.layerId)
                  if (mergedLayerId) entity.layerId = mergedLayerId
                  return entity
                })
              }
            }
            if (options?.recordHistory && historyManager) {
              const command = new AddEntityCommand(
                entities,
                viewManager as unknown as CADViewManager
              )
              historyManager.addCommandAndExecute(command)
              return
            }
            viewManager.addEntities(entities)
          })
          const bounds = new THREE.Box3().setFromObject(result.objectManager.getSceneGroup())
          if (!bounds.isEmpty()) {
            handle.boundingBox = bounds
          }
        } catch {
          /* 忽略 */
        }
      },

      addLayer(input: CadLayerCreateInput, options?: CadLayerMutationOptions): Layer | undefined {
        let layer: Layer | undefined
        withActiveHistory((viewManager, historyManager) => {
          const hasIdConflict =
            input.id != null && viewManager.dataManager?.getLayer?.(input.id) != null
          if (options?.recordHistory && historyManager && !hasIdConflict) {
            const command = new CreateLayerCommand(input, viewManager as unknown as CADViewManager)
            try {
              historyManager.addCommandAndExecute(command)
              layer = command.getLayers()[0]
              return
            } catch {
              // 命令执行异常：退回本地新增路径
            }
          }
          layer = new Layer({
            ...(omitBy(cloneDeep(input), isNil) as Partial<ILayer>),
            id: input.id ?? nanoid(),
          })
          viewManager.addLayer(layer)
        })
        return layer
      },

      updateLayer(
        layerId: string,
        patch: CadLayerUpdatePatch,
        options?: CadLayerMutationOptions
      ): void {
        withActiveHistory((viewManager, historyManager) => {
          if (options?.recordHistory && historyManager) {
            const command = new UpdateLayerCommand(
              layerId,
              patch,
              viewManager as unknown as CADViewManager
            )
            historyManager.addCommandAndExecute(command)
          } else {
            const layer = viewManager.dataManager.getLayer(layerId)
            if (layer) {
              const previousVisible = layer.visible
              Object.assign(layer, omitBy(cloneDeep(patch), isNil) as Partial<ILayer>)
              viewManager.updateLayer(layer, previousVisible)
            }
          }
        })
      },

      deleteLayer(layerId: string, options?: CadLayerMutationOptions): void {
        withActiveHistory((viewManager, historyManager) => {
          if (options?.recordHistory && historyManager) {
            const command = new RemoveLayerCommand(
              layerId,
              viewManager as unknown as CADViewManager
            )
            historyManager.addCommandAndExecute(command)
          } else {
            viewManager.dataManager.deleteLayer(layerId)
          }
        })
      },

      addLayersAndEntities(
        layers: CadLayerCreateInput[],
        entities: CadEntityData[],
        options?: { recordHistory?: boolean }
      ): void {
        const typedEntities = entities as unknown as IEntity[]
        withActiveHistory((viewManager, historyManager) => {
          if (options?.recordHistory && historyManager) {
            const layerCommand = new CreateLayerCommand(
              layers,
              viewManager as unknown as CADViewManager
            )
            const entityCommand = new AddEntityCommand(
              typedEntities,
              viewManager as unknown as CADViewManager
            )
            const command = new CompositeCommand([layerCommand, entityCommand])
            historyManager.addCommandAndExecute(command)
          } else {
            const newLayers = layers.map((layer) => {
              return new Layer({
                ...(omitBy(cloneDeep(layer), isNil) as Partial<ILayer>),
                id: layer.id ?? nanoid(),
              })
            })
            viewManager.addLayers(newLayers)
            viewManager.addEntities(typedEntities)
          }
        })
      },

      getPlane(): IPlane | null {
        const plane = result.dataManager.plane
        return plane
      },

      setPlane(plane: Partial<IPlaneMetaData>): void {
        result.dataManager.updatePlane(plane)
        result.objectManager.updateGroupByPlane(result.dataManager.plane)
        plugin.refreshAssetView(id)
      },

      getLayers(): CadLayer[] {
        return result.dataManager.layers
      },

      setActiveLayer(_layerId: string): void {
        // 预留接口：需要 ViewManager 支持活跃图层设置
      },
    }

    this.assets.set(id, handle)
    log.debug(
      `[CadPlugin] handle created, id=${id}, entities=${result.dataManager.entities.length}, layers=${result.dataManager.layers.length}, assets=${this.assets.size}`
    )
    return handle
  }

  /** 桥接 CAD 内部事件到引擎 EventBus，并实现 SelectionService 双向同步 */
  private bridgeSelection(): void {
    // 1. 全局容器的 HistoryManager / EntitySelector 事件桥接
    this._bridgeContainerEvents(this.cadContainer)

    // 2. SelectionService (selection.changed) → 活跃 CAD 会话 EntitySelector 同步
    const onSelectionChanged = (evt: { selected: PickResult[]; previous: PickResult[] }) => {
      if (this.bridgingSelection) return
      const cadIds = evt.selected
        .filter((item) => item.kind === 'cad' && item.entityId)
        .map((item) => item.entityId!)
      this.bridgingSelection = true
      try {
        this.syncSelectionFromService(cadIds)
      } finally {
        this.bridgingSelection = false
      }
    }
    this.context.events.on('selection.changed', onSelectionChanged)
    this.bridgeCleanups.push(() => this.context.events.off('selection.changed', onSelectionChanged))
  }

  /** 桥接容器内 CAD 事件到引擎 EventBus */
  private _bridgeContainerEvents(container: Container): void {
    // 1. HistoryManager → cad.historyChanged / cad.undoRedo 事件
    try {
      const hm = container.get<ICadHistoryManager>(CAD_TYPES.HistoryManager)
      const onHistoryChanged = () => {
        this.context.events.emit('cad.historyChanged', {
          assetId: this.activeSessionId ?? '',
          canUndo: hm.canUndo,
          canRedo: hm.canRedo,
        })
      }
      const onUndoRedo = () => {
        this.context.events.emit('cad.undoRedo', {
          assetId: this.activeSessionId ?? '',
          canUndo: hm.canUndo,
          canRedo: hm.canRedo,
        })
      }
      hm.on('onStateChanged', onHistoryChanged)
      hm.on('undoRedo', onUndoRedo)
      this.bridgeCleanups.push(() => hm.off('onStateChanged', onHistoryChanged))
      this.bridgeCleanups.push(() => hm.off('undoRedo', onUndoRedo))
    } catch {
      log.warn('[CadPlugin] _bridgeContainerEvents: HistoryManager 不可用，跳过历史事件桥接')
    }

    // 2. EntitySelector → cad.selectionChanged 事件 + 回写 SelectionService
    try {
      const es = container.get<ICadEntitySelector>(CAD_TYPES.EntitySelector)
      const onSelectorChanged = (entities: Array<{ id: string }>) => {
        if (this.bridgingSelection) return
        const entityIds = (entities ?? []).map((e) => e.id)
        this.context.events.emit('cad.selectionChanged', {
          assetId: this.activeSessionId ?? '',
          entityIds,
        })

        // 回写 SelectionService
        this.bridgingSelection = true
        try {
          const selectionService = this.context.getService('SelectionService') as {
            set(results: PickResult[]): void
          }
          const pickResults: PickResult[] = entityIds.map((eid: string) => ({
            assetId: this.activeSessionId ?? '',
            kind: this.kind,
            entityId: eid,
            point: new THREE.Vector3(),
            distance: 0,
          }))
          selectionService.set(pickResults)
        } catch {
          // SelectionService 不可用时静默忽略
        } finally {
          this.bridgingSelection = false
        }
      }
      es.on('onSelectorChanged', onSelectorChanged as (...args: unknown[]) => void)
      this.bridgeCleanups.push(() =>
        es.off('onSelectorChanged', onSelectorChanged as (...args: unknown[]) => void)
      )
    } catch {
      log.warn('[CadPlugin] _bridgeContainerEvents: EntitySelector 不可用，跳过选择事件桥接')
    }

    // 3. CADController → cad.toolChanged 事件
    try {
      const controller = container.get<CADController>(CAD_TYPES.CADController)
      const onToolChanged = (payload: {
        curTool?: { name?: unknown } | null
        lastTool?: { name?: unknown } | null
      }) => {
        this.context.events.emit('cad.toolChanged', {
          assetId: this.activeSessionId ?? '',
          curToolName: resolveCadToolName(payload?.curTool?.name),
          lastToolName: resolveCadToolName(payload?.lastTool?.name),
        })
      }
      controller.on('onToolChanged', onToolChanged)
      this.bridgeCleanups.push(() => controller.off('onToolChanged', onToolChanged))
    } catch {
      log.warn('[CadPlugin] _bridgeContainerEvents: CADController 不可用，跳过工具事件桥接')
    }
  }

  // ─── 多会话管理 ────────────────────────────────────────

  /**
   * 获取当前活跃 CAD 会话。
   *
   * @returns 当前活跃资产；无活跃会话返回 undefined
   */
  getActive(): CadHandle | undefined {
    if (!this.activeSessionId) return undefined
    return this.assets.get(this.activeSessionId)
  }

  /**
   * 获取当前活跃会话 ID。
   *
   * @returns 当前活跃资产 id；无活跃会话返回 null
   */
  getActiveId(): AssetId | null {
    return this.activeSessionId
  }

  /**
   * 切换活跃会话。
   *
   * @param id - 目标资产 id；传 null 表示清空活跃会话
   */
  setActive(id: AssetId | null): void {
    if (id === this.activeSessionId) {
      if (id !== null) {
        this.refreshAssetView(id)
      }
      return
    }
    if (id !== null && !this.assets.has(id)) {
      throw new Error(`[CadPlugin] setActive 失败：未找到资产 "${id}"`)
    }

    const previousId = this.activeSessionId
    this.activeSessionId = id
    this.bindActiveHandleToView()

    this.context.events.emit('cad.activeChanged', { assetId: id, previousId })
  }

  private bindActiveHandleToView(): void {
    const activeHandle = this.getActive()
    if (!activeHandle) return
    this.bindHandleToView(activeHandle)
  }

  private bindHandleToView(handle: Pick<CadHandle, 'dataManager' | 'objectManager'>): boolean {
    try {
      const viewManager = this.cadContainer.get<ICadViewManager>(CAD_TYPES.CADViewManager)
      viewManager.set(handle.dataManager, handle.objectManager)
      return true
    } catch {
      return false
    }
  }

  private ensureHandleIsActive(id: AssetId): void {
    if (this.activeSessionId !== id) {
      this.setActive(id)
    }
  }

  /**
   * 外部修改 CAD 实体组位姿后，刷新该资产关联的活跃工作视图。
   *
   * @param id - 已加载的 CAD 资产 id；非活跃资产不会触发视图重绑
   */
  refreshAssetView(id: AssetId): void {
    if (this.activeSessionId !== id) return
    this.bindActiveHandleToView()
  }

  private runHandleDataAction(
    result: CadLoadResult,
    action: (viewManager: ICadViewManager, historyManager: ICadHistoryManager | null) => void
  ): void {
    const viewManager = {
      dataManager: result.dataManager,
      objectManager: result.objectManager,
      planeHelper: {
        normal: result.dataManager.plane.normal,
        constant: -result.dataManager.plane.normal.dot(result.dataManager.plane.origin),
      },
      set: () => {},
      getEntity: (entityId: string) => result.dataManager.getEntity(entityId),
      addEntities: (entities: IEntity[]) => {
        result.dataManager.addEntities(entities)
        result.objectManager.addEntities(entities)
      },
      deleteEntities: (entities: Array<{ id: string }>) => {
        const typedEntities = entities
          .map((entity) => result.dataManager.getEntity(entity.id))
          .filter((entity): entity is IEntity => entity != null)
        result.dataManager.deleteEntities(typedEntities)
        result.objectManager.deleteEntities(typedEntities)
      },
      updateEntities: (entities: IEntity[], updateOptions?: IUpdateOptions) => {
        result.dataManager.updateEntities(entities)
        result.objectManager.updateEntities(entities, updateOptions)
      },
      addLayer: (layer: Layer) => {
        result.dataManager.addLayer(layer)
      },
      deleteLayer: (id: string) => {
        result.dataManager.deleteLayer(id)
      },
      updateLayer: (
        layer: Layer,
        previousVisible = result.dataManager.getLayer(layer.id)?.visible
      ) => {
        result.dataManager.updateLayer(layer)
        if (previousVisible !== undefined && previousVisible !== layer.visible) {
          result.objectManager.setEntitiesVisible(
            result.dataManager.entities.filter((entity) => entity.layerId === layer.id),
            layer.visible
          )
        }
      },
      addLayers: (layers: Layer[]) => {
        result.dataManager.addLayers(layers)
      },
    } as unknown as ICadViewManager

    action(viewManager, null)
  }

  private withBoundAssetView<T>(assetId: AssetId, action: () => T): T {
    const handle = this.assets.get(assetId)
    if (!handle) {
      throw new Error(`[CadPlugin] 未找到资产 "${assetId}"`)
    }

    const viewManager = this.cadContainer.get<ICadViewManager>(CAD_TYPES.CADViewManager)
    const previousDataManager = viewManager.dataManager
    const previousObjectManager = viewManager.objectManager
    viewManager.set(handle.dataManager, handle.objectManager)

    try {
      return action()
    } finally {
      viewManager.set(previousDataManager, previousObjectManager)
    }
  }

  private async withBoundAssetViewAsync<T>(assetId: AssetId, action: () => Promise<T>): Promise<T> {
    const handle = this.assets.get(assetId)
    if (!handle) {
      throw new Error(`[CadPlugin] 未找到资产 "${assetId}"`)
    }

    const viewManager = this.cadContainer.get<ICadViewManager>(CAD_TYPES.CADViewManager)
    const previousDataManager = viewManager.dataManager
    const previousObjectManager = viewManager.objectManager
    viewManager.set(handle.dataManager, handle.objectManager)

    try {
      return await action()
    } finally {
      viewManager.set(previousDataManager, previousObjectManager)
    }
  }
}
